/**
 * OrderViewsMenu — Orders' part of the Sales flyout (O3; ROLLOUT-ORDERS-SHELL.md §2.5, §3 D1/D8,
 * §4.2). The registry's flyout group renders it in two places, as CustomerViewsMenu is: nested
 * under Orders in the Sales flyout (rail), and inline under the Orders row (expanded sidebar,
 * "Show order views").
 *
 *   Views   D1's visible subset: All, Open, Completed, Cancelled. The server also has
 *           awaiting_delivery and awaiting_payment (O1a); they stay hidden until their fields
 *           move (D1), but a link to one still lists, and then no entry here is current.
 *   Counts  GET /api/orders/stats/view-counts for the q of the orders list on screen (the URL's,
 *           which the list writes after its 300 ms debounce, so the counts never follow single
 *           keystrokes). Cached 60 s on this device, per q, shared by every menu mounted. A clear
 *           (a status change, a cancel, an amendment) refetches once in every menu still mounted.
 *           The hook is Customers polish 15's, copied rather than shared (this slice does not
 *           generalise the Customers hook).
 *
 * Every entry is a link (a menuitem): choosing one writes ?view= and nothing else. Colours are
 * tokens only.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cachedFetch } from '../../services/apiCache';
import { ORDERS_ROOT, orderViewHref, parseOrderLocation, useOrderListDefaults } from './useOrderRoute';
import '../layout/Flyout.css';
import './OrderViewsMenu.css';

export const ORDER_VIEW_COUNTS_PATH = '/api/orders/stats/view-counts';
export const ORDER_VIEW_COUNTS_TTL_MS = 60 * 1000;

/** D1's visible views, in menu order. `tone` is the --status-<tone> family of its dot. */
export const ORDER_VIEW_ENTRIES = [
  { id: 'all', label: 'All', tone: 'expired' },
  { id: 'open', label: 'Open', tone: 'pending' },
  { id: 'completed', label: 'Completed', tone: 'success' },
  { id: 'cancelled', label: 'Cancelled', tone: 'error' }
];

const HIDDEN_VIEW_LABELS = { awaiting_delivery: 'Awaiting delivery', awaiting_payment: 'Awaiting payment' };

export const orderViewLabel = (view) => {
  const entry = ORDER_VIEW_ENTRIES.find((v) => v.id === view);
  return entry ? entry.label : (HIDDEN_VIEW_LABELS[view] || ORDER_VIEW_ENTRIES[0].label);
};

const onOrders = (pathname) => pathname === ORDERS_ROOT || String(pathname || '').startsWith(`${ORDERS_ROOT}/`);

/** The counts URL for a list's q (q from 2 characters, as the list sends it). */
export function orderViewCountsUrl({ q = '' } = {}) {
  const term = String(q || '').trim();
  return term.length >= 2 ? `${ORDER_VIEW_COUNTS_PATH}?${new URLSearchParams({ q: term })}` : ORDER_VIEW_COUNTS_PATH;
}

// url -> { at, promise }: one request per q per 60 s, shared by every menu mounted.
const countsCache = new Map();

// Bumped by every clear. A mounted useOrderViewCounts subscribes to it, so a clear while it is on
// screen refetches once: the first hook to run misses the emptied cache, the others share its
// request. Nothing cleared, nothing sent.
let countsVersion = 0;
const versionListeners = new Set();
const subscribeVersion = (listener) => {
  versionListeners.add(listener);
  return () => versionListeners.delete(listener);
};
const getVersion = () => countsVersion;

/** Forget the cached counts; every mounted counts hook fetches them again (once). */
export function clearOrderViewCounts() {
  countsCache.clear();
  countsVersion += 1;
  versionListeners.forEach((listener) => listener());
}

function loadCounts(url) {
  const hit = countsCache.get(url);
  if (hit && Date.now() - hit.at < ORDER_VIEW_COUNTS_TTL_MS) return hit.promise;
  const promise = cachedFetch(url, { skipCache: true }).then((body) => body?.views || null);
  countsCache.set(url, { at: Date.now(), promise });
  promise.catch(() => {
    if (countsCache.get(url)?.promise === promise) countsCache.delete(url);
  });
  return promise;
}

/** The views block for the orders list on screen (or the bare counts elsewhere). */
export function useOrderViewCounts() {
  const location = useLocation();
  const list = onOrders(location.pathname) ? parseOrderLocation(location.pathname, location.search).list : null;
  const url = orderViewCountsUrl({ q: list?.q });
  const version = useSyncExternalStore(subscribeVersion, getVersion, getVersion);
  const [state, setState] = useState({ url: null, views: null });
  useEffect(() => {
    let alive = true;
    loadCounts(url)
      .then((views) => { if (alive) setState({ url, views }); })
      .catch(() => { /* no counts: the views still work as links */ });
    return () => { alive = false; };
  }, [url, version]);
  // Counts for another q are not this list's: show none until this q's arrive. After a clear the
  // same q's last counts stay until the fresh ones land (no blank flash).
  return state.url === url ? state.views : null;
}

/**
 * @param {object} props
 * @param {boolean} [props.inline] the expanded sidebar's child list (no heading)
 */
export default function OrderViewsMenu({ inline = false }) {
  const location = useLocation();
  const counts = useOrderViewCounts();
  // O4: the user's default view is the bare /orders, so it is current there and its link is bare.
  const defaults = useOrderListDefaults();
  const onList = location.pathname === ORDERS_ROOT;
  const activeView = onList ? parseOrderLocation(location.pathname, location.search, defaults).list.view : null;

  return (
    <div className={`order-views-menu${inline ? ' order-views-menu-inline' : ''}`}>
      <div role="group" aria-label="Order views">
        {!inline && <div className="shell-flyout-heading" aria-hidden="true">Order views</div>}
        <ul className="shell-flyout-list" role="none">
          {ORDER_VIEW_ENTRIES.map((v) => {
            const count = counts && Number.isFinite(counts[v.id]) ? counts[v.id] : null;
            const current = activeView === v.id;
            return (
              <li key={v.id} role="none">
                <Link
                  to={orderViewHref(v.id, location, defaults)}
                  role="menuitem"
                  aria-label={count === null ? v.label : `${v.label} (${count})`}
                  aria-current={current ? 'page' : undefined}
                  className={`shell-flyout-item order-views-item${current ? ' active' : ''}`}
                  style={{ '--order-view-tone': `var(--status-${v.tone})` }}
                >
                  <span className="order-views-dot" aria-hidden="true" />
                  <span className="order-views-label">{v.label}</span>
                  <span className="order-views-count" aria-hidden="true">{count === null ? '' : count}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
